import { Modal, Select, Space, Typography } from "antd"
import React, { useMemo, useState } from "react"
import { useNavigate } from "react-router"

import { RoutesPaths } from "../../../../../constants/routes.ts"
import { PROPOSAL_STATUSES } from "../../../../Proposals/constants/proposal-statuses-keys.ts"
import type { LeadProposal } from "../../../types/lead.documents.types.ts"

const { Text } = Typography

type OptionType = {
  value: string
  label: string
  uploaded: boolean
  is_version_2: boolean
}

interface LeadViewContractsCreateModalProps {
  leadId: string
  proposals?: LeadProposal[] | null
  onCreateFromProposal: (proposalId: string) => Promise<void>
  onClose: () => void
}

export function LeadViewContractsCreateModal({
  leadId,
  proposals,
  onCreateFromProposal,
  onClose
}: LeadViewContractsCreateModalProps) {
  const navigate = useNavigate()

  const [selectedProposal, setSelectedProposal] = useState<OptionType | null>(
    null
  )
  const [confirmLoading, setConfirmLoading] = useState(false)

  const toggleOpen = () => {
    if (confirmLoading) return
    onClose()
  }

  const approvedProposals: OptionType[] = useMemo(() => {
    if (!proposals?.length) return []

    const allowedStatuses = new Map<string, boolean>([
      [PROPOSAL_STATUSES.APPROVED, true],
      [PROPOSAL_STATUSES.SENT_TO_CLIENT, true]
    ])

    return proposals.reduce<OptionType[]>((acc, p) => {
      // if (p?.can_create_contract && allowedStatuses.has(p?.status)) { // when SENT_TO_CLIENT - for some reason can_create_contract = false
      if (allowedStatuses.has(p?.status)) {
        const isLastFileUploaded = !!p?.attachments?.at(-1)?.extra?.uploaded

        acc.push({
          value: p?.id,
          label: p?.proposal_id,
          uploaded: isLastFileUploaded,
          is_version_2: p?.is_version_2
        })
      }

      return acc
    }, [])
  }, [proposals])

  const hasApprovedProposals = approvedProposals?.length > 0

  const onConfirm = async () => {
    const proposalId = selectedProposal?.value
    if (!proposalId) return

    try {
      if (selectedProposal?.uploaded) {
        setConfirmLoading(true)
        await onCreateFromProposal(proposalId)
        setSelectedProposal(null)
        onClose()
        return
      }

      if (selectedProposal?.is_version_2) {
        return navigate(
          RoutesPaths.contractDocument.create.url(leadId, { proposalId })
        )
      } else {
        return navigate(RoutesPaths.contract.add(leadId, proposalId))
      }
    } finally {
      setConfirmLoading(false)
    }
  }

  return (
    <Modal
      okText="Confirm"
      cancelText="Cancel"
      onOk={onConfirm}
      onCancel={toggleOpen}
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: !selectedProposal || confirmLoading }}
      cancelButtonProps={{ disabled: confirmLoading }}
      open
      title={
        <Typography.Title level={3} style={{ margin: 0, textAlign: "start" }}>
          Please select proposal
        </Typography.Title>
      }
      centered={true}
      maskClosable={false}
      width={600}
    >
      <Space direction={"vertical"} className={"w-full"}>
        <Select
          value={selectedProposal?.value}
          disabled={!hasApprovedProposals}
          onChange={(_, o) => setSelectedProposal(o as OptionType)}
          className={"w-full"}
          placeholder={"Select proposal"}
          options={approvedProposals}
        />
        {!hasApprovedProposals && (
          <Text type="danger">
            For creating a contract, you need to have at least one approved
            proposal.
          </Text>
        )}
      </Space>
    </Modal>
  )
}
